import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ReviewForm from './ReviewForm';

const AddGameToReview = ({ onClose }) => {
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();

    const [step, setStep] = useState(1); // 1: search, 2: add/confirm, 3: review
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedGame, setSelectedGame] = useState(null);

    // Manual game entry
    const [manualGame, setManualGame] = useState({
        name: '',
        developer: '',
        publisher: '',
        releaseYear: '',
        platforms: [],
        genres: [],
        description: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSearch = async (e) => {
        if (e) e.preventDefault();

        if (!searchQuery.trim() || searchQuery.length < 2) {
            setError('Please enter at least 2 characters to search');
            return;
        }

        setSearching(true);
        setError('');

        try {
            const response = await axios.get(`/api/games/search?q=${encodeURIComponent(searchQuery)}`);
            setSearchResults(response.data.games || []);

            if (response.data.games.length === 0) {
                setError('No games found. You can add it manually below.');
            }
        } catch (err) {
            setError('Error searching games');
            console.error('Search error:', err);
        } finally {
            setSearching(false);
        }
    };

    const selectGame = (game) => {
        setSelectedGame(game);
        setStep(3); // Go to review step
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();

        if (!manualGame.name.trim()) {
            setError('Game name is required');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/games', manualGame, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                setSelectedGame(response.data.game);
                setStep(3); // Go to review step
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add game');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReviewSuccess = () => {
        if (onClose) {
            onClose();
        } else {
            navigate('/game-reviews');
        }
    };

    if (!isAuthenticated) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>Please log in to review games</p>
            </div>
        );
    }

    return (
        <div className="add-game-modal" style={{ padding: '32px', maxWidth: '100%', margin: '0' }}>
            {step === 1 && (
                <div>
                    <h2 style={{ fontSize: '1.8rem', marginBottom: '8px' }}>Find a Game to Review</h2>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '1rem' }}>
                        Search for any game or add it manually
                    </p>

                    {/* Search Form */}
                    <form onSubmit={handleSearch} style={{ marginBottom: '30px' }}>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search for a game (e.g., Elden Ring, Minecraft...)"
                                className="form-control"
                                style={{
                                    flex: 1,
                                    minWidth: '250px',
                                    padding: '14px 16px',
                                    fontSize: '1rem',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border)',
                                    backgroundColor: 'var(--card-bg)',
                                    color: 'var(--text)'
                                }}
                            />
                            <button
                                type="submit"
                                disabled={searching}
                                className="btn btn-primary"
                                style={{ 
                                    padding: '14px 28px', 
                                    whiteSpace: 'nowrap',
                                    fontSize: '1rem',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    cursor: searching ? 'not-allowed' : 'pointer',
                                    opacity: searching ? 0.6 : 1
                                }}
                            >
                                {searching ? '🔍 Searching...' : '🔍 SEARCH'}
                            </button>
                        </div>
                    </form>

                    {error && <div className="error-message" style={{ marginBottom: '20px', padding: '14px 16px', backgroundColor: 'var(--error-bg)', color: 'var(--error-text)', borderRadius: '8px', border: '1px solid var(--error-text)' }}>{error}</div>}

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <div style={{ marginBottom: '40px' }}>
                            <h3>Search Results:</h3>
                            <div style={{ display: 'grid', gap: '12px', marginTop: '16px' }}>
                                {searchResults.map((game) => (
                                    <div
                                        key={game._id}
                                        onClick={() => selectGame(game)}
                                        className="game-search-result"
                                        style={{
                                            padding: '16px',
                                            backgroundColor: 'var(--card-bg)',
                                            border: '1px solid var(--border)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--accent)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--border)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                            <div>
                                                <h4 style={{ marginBottom: '8px' }}>{game.name}</h4>
                                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '4px' }}>
                                                    {game.developer && `Developer: ${game.developer}`}
                                                    {game.releaseYear && ` • ${game.releaseYear}`}
                                                </p>
                                                {game.platforms && game.platforms.length > 0 && (
                                                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                                        {game.platforms.join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                            {game.averageRating > 0 && (
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontWeight: 'bold', color: 'var(--accent)' }}>
                                                        ⭐ {game.averageRating.toFixed(1)}
                                                    </div>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                        {game.totalReviews} reviews
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Manual Entry Form */}
                    <div style={{
                        marginTop: '48px',
                        paddingTop: '40px',
                        borderTop: '2px solid var(--border)'
                    }}>
                        <h3 style={{ fontSize: '1.4rem', marginBottom: '12px' }}>Can't find it? Add the game manually</h3>
                        <form onSubmit={handleManualSubmit} style={{ marginTop: '24px' }}>
                            <div style={{ display: 'grid', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.95rem' }}>
                                        Game Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={manualGame.name}
                                        onChange={(e) => setManualGame({ ...manualGame, name: e.target.value })}
                                        placeholder="e.g., The Legend of Zelda"
                                        required
                                        className="form-control"
                                        style={{ 
                                            width: '100%',
                                            padding: '12px 16px',
                                            fontSize: '1rem',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border)',
                                            backgroundColor: 'var(--card-bg)',
                                            color: 'var(--text)'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.95rem' }}>
                                            Developer
                                        </label>
                                        <input
                                            type="text"
                                            value={manualGame.developer}
                                            onChange={(e) => setManualGame({ ...manualGame, developer: e.target.value })}
                                            placeholder="e.g., Nintendo"
                                            className="form-control"
                                            style={{ 
                                                width: '100%',
                                                padding: '12px 16px',
                                                fontSize: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid var(--border)',
                                                backgroundColor: 'var(--card-bg)',
                                                color: 'var(--text)'
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.95rem' }}>
                                            Release Year
                                        </label>
                                        <input
                                            type="number"
                                            value={manualGame.releaseYear}
                                            onChange={(e) => setManualGame({ ...manualGame, releaseYear: e.target.value })}
                                            placeholder="2023"
                                            min="1970"
                                            max="2030"
                                            className="form-control"
                                            style={{ 
                                                width: '100%',
                                                padding: '12px 16px',
                                                fontSize: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid var(--border)',
                                                backgroundColor: 'var(--card-bg)',
                                                color: 'var(--text)'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.95rem' }}>
                                        Description (optional)
                                    </label>
                                    <textarea
                                        value={manualGame.description}
                                        onChange={(e) => setManualGame({ ...manualGame, description: e.target.value })}
                                        placeholder="Brief description of the game..."
                                        rows="3"
                                        maxLength="1000"
                                        className="form-control"
                                        style={{ 
                                            width: '100%',
                                            padding: '12px 16px',
                                            fontSize: '1rem',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border)',
                                            backgroundColor: 'var(--card-bg)',
                                            color: 'var(--text)',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: '28px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn btn-primary"
                                    style={{ 
                                        flex: '1', 
                                        minWidth: '200px',
                                        padding: '14px 24px',
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        opacity: submitting ? 0.6 : 1
                                    }}
                                >
                                    {submitting ? '⏳ Adding...' : '➕ ADD GAME & WRITE REVIEW'}
                                </button>
                                {onClose && (
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="btn btn-secondary"
                                        style={{ 
                                            minWidth: '120px',
                                            padding: '14px 24px',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        CANCEL
                                    </button>
                                )}
                            </div>

                            <p style={{ 
                                marginTop: '16px', 
                                fontSize: '0.9rem', 
                                color: 'var(--success-text)',
                                backgroundColor: 'var(--success-bg)',
                                padding: '10px 14px',
                                borderRadius: '6px',
                                display: 'inline-block'
                            }}>
                                💰 You'll earn 10 points for adding a new game to the database!
                            </p>
                        </form>
                    </div>
                </div>
            )}

            {step === 3 && selectedGame && (
                <div>
                    <div style={{
                        marginBottom: '32px',
                        padding: '24px',
                        backgroundColor: 'var(--card-bg)',
                        borderRadius: '12px',
                        border: '2px solid var(--accent)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '500' }}>
                            Writing review for:
                        </h3>
                        <h2 style={{ fontSize: '1.8rem', marginTop: '4px', color: 'var(--accent)', fontWeight: 'bold' }}>
                            {selectedGame.name}
                        </h2>
                        {selectedGame.developer && (
                            <p style={{ color: 'var(--text-secondary)', marginTop: '8px', fontSize: '1rem' }}>
                                by {selectedGame.developer}
                            </p>
                        )}
                    </div>

                    <ReviewForm
                        targetType="Game"
                        targetId={selectedGame._id}
                        onSuccess={handleReviewSuccess}
                        onCancel={() => {
                            setStep(1);
                            setSelectedGame(null);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default AddGameToReview;
